import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, PLATFORM_ID, SecurityContext, effect, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * `[innerHTML]` that keeps the server-rendered children on hydration.
 *
 * Angular re-applies `[innerHTML]` while hydrating, which replaces every child node.
 * Chrome then reports a new LCP for the largest rich-text paragraph at JS time instead
 * of first paint. Same sanitization as `[innerHTML]`; only the first browser write is
 * skipped, and only when the markup is identical.
 */
@Directive({
  selector: '[appHtml]',
})
export class HtmlDirective {
  readonly appHtml = input<string | SafeHtml | null | undefined>();

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly sanitizer = inject(DomSanitizer);
  private hydrating = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    effect(() => {
      const html = this.sanitizer.sanitize(SecurityContext.HTML, this.appHtml() ?? '') ?? '';
      if (this.hydrating) {
        this.hydrating = false;
        if (this.el.hasChildNodes() && sameMarkup(this.el, html)) return;
      }
      this.el.innerHTML = html;
    });
  }
}

/** Compares after browser serialization, so quoting and entity differences don't count. */
function sameMarkup(el: HTMLElement, html: string): boolean {
  const template = el.ownerDocument.createElement('template');
  template.innerHTML = html;
  return template.innerHTML === el.innerHTML;
}
